import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { formatCurrencyWithDecimals } from 'src/utils/formatAmout';
import CapitalBalanceTerminationKpiDialog from './CapitalBalanceTerminationKpiDialog';
import useStyles from './terminationKpiStyles';

const KpiCard = ({ icon, iconClass, label, value, onClick }) => {
  const { classes } = useStyles();

  return (
    <Box
      className={clsx(classes.card, onClick && classes.cardClickable)}
      onClick={onClick}
    >
      <Box className={clsx(classes.cardIcon, iconClass)}>{icon}</Box>
      <Box>
        <Typography className={classes.cardLabel} component="p" variant="body2">
          {label}
        </Typography>
        <Typography color="primary" component="p" variant="h2">
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

KpiCard.propTypes = {
  icon: PropTypes.node.isRequired,
  iconClass: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  onClick: PropTypes.func
};

KpiCard.defaultProps = {
  onClick: undefined
};

const CapitalBalanceKpiCards = ({ config }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { activeContracts, currentMonthFinalBalance, termination } = useSelector(
    config.selectors.selectKpis
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    dispatch(config.actions.loadKpis());
  }, [dispatch, config.actions]);

  // The backend caps offendingContracts, so its length is not the KPI.
  const offenderCount = termination.offendingContractCount;
  const hasOffenders = offenderCount > 0;

  return (
    <Box>
      <Box className={classes.cardsRow}>
        <KpiCard
          icon={<DescriptionOutlinedIcon />}
          iconClass={classes.cardIconInfo}
          label={t('CAPITAL_BALANCE.KPI_ACTIVE_CONTRACTS')}
          value={activeContracts}
        />
        <KpiCard
          icon={<AttachMoneyIcon />}
          iconClass={classes.cardIconNeutral}
          label={t('CAPITAL_BALANCE.KPI_CURRENT_MONTH_BALANCE')}
          value={formatCurrencyWithDecimals(currentMonthFinalBalance, 2)}
        />
        <KpiCard
          icon={hasOffenders ? <ErrorOutlineIcon /> : <CheckCircleIcon />}
          iconClass={
            hasOffenders ? classes.cardIconError : classes.cardIconOk
          }
          label={t('CAPITAL_BALANCE.KPI_CONTRACTS_WITH_DIFFERENCE')}
          value={offenderCount}
          onClick={hasOffenders ? () => setDialogOpen(true) : undefined}
        />
      </Box>
      <CapitalBalanceTerminationKpiDialog
        open={dialogOpen}
        handleClose={() => setDialogOpen(false)}
        config={config}
        total={termination.total}
        contractCount={termination.contractCount}
        offenderCount={offenderCount}
        offendingContracts={termination.offendingContracts}
      />
    </Box>
  );
};

CapitalBalanceKpiCards.propTypes = {
  config: PropTypes.object.isRequired
};

export default CapitalBalanceKpiCards;
